/**
 * Auto-recarga la página una sola vez si un chunk lazy falla al cargar
 * (típico tras deploy con SW cache stale). Session-guarded para evitar
 * bucles infinitos.
 *
 * Cubre los tres mensajes distintos que dan los navegadores:
 * - `Failed to fetch dynamically imported module` (Chrome/Edge)
 * - `Importing a module script failed` (Safari)
 * - `error loading dynamically imported module` (Firefox)
 * - `Loading chunk N failed` (React Router / Vite chunks)
 *
 * Uso:
 *   const loader = () => lazyWithRefresh(async () => {
 *     const { LoginPage } = await import('...');
 *     return { Component: LoginPage };
 *   });
 *
 * Portado de ManuJob (`src/lib/lazyRetry.js`), adaptado a router.tsx-style
 * loaders (que devuelven `{ Component }`, no un componente React.lazy).
 */

/**
 * Los mensajes de arriba, exportados porque los dos boundaries también tienen
 * que reconocerlos: `RouteErrorElement` para enseñar el texto de «se acaba de
 * publicar una versión» en vez del error crudo, y no sólo esta función.
 * Estaba tecleada entera tres veces.
 */
export const CHUNK_ERROR_RX =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk|Loading CSS chunk/i;

/**
 * La guarda anti-bucle, exportada **porque hay que poder quitarla desde fuera**.
 *
 * Los dos boundaries la borran cuando alguien pulsa «recargar»: es la forma de
 * decir «esta vez lo pide una persona, no el bucle». Y los dos la borraban
 * escribiendo `'__reload_after_chunk_error'` a mano —**sin el `_at`**—, así que
 * borraban una clave que nadie escribe.
 *
 * El efecto era justo el contrario del que se buscaba: durante los 60 segundos
 * de `RELOAD_COOLDOWN_MS`, quien pulsaba el botón se quedaba con la guarda
 * puesta, no se recargaba nada, y volvía a salir la misma pantalla de error. El
 * botón que existe para salir del atasco era el único que no servía para salir.
 *
 * Se exporta en vez de arreglar las dos copias porque arreglarlas dejaba tres
 * sitios donde se escribe la misma cadena, que es de donde venía el fallo.
 */
export const RELOAD_GUARD_KEY = '__reload_after_chunk_error_at';
// Ventana durante la que consideramos un reload "reciente". Si el próximo
// fallo llega DESPUÉS de esta ventana, permitimos otro reload — típico
// cuando hay varios deploys seguidos que invalidan los chunks múltiples
// veces mientras el usuario lleva la app abierta.
const RELOAD_COOLDOWN_MS = 60_000;

export async function lazyWithRefresh<T>(loader: () => Promise<T>): Promise<T> {
  try {
    return await loader();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (!CHUNK_ERROR_RX.test(msg)) throw err;

    // Sí es un chunk error — típicamente SW cache stale tras un deploy.
    // Estrategia: reload una vez, pero con cooldown por si hay múltiples
    // deploys consecutivos que también invalidan el bundle nuevo.
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
    const now = Date.now();
    const withinCooldown = lastReloadAt > 0 && now - lastReloadAt < RELOAD_COOLDOWN_MS;

    if (!withinCooldown) {
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
      window.location.reload();
      return new Promise<T>(() => {});
    }

    // Aún dentro del cooldown — el reload anterior no arregló el problema
    // (o hay un tercer deploy en pocos segundos). Reintenta una vez el
    // loader crudo; si vuelve a fallar, el error se propaga al
    // ErrorBoundary con su UI amigable.
    return loader();
  }
}
